


































































































































import Vue, { PropType } from "vue";
import InfiniteLoading from "vue-infinite-loading";
import {
  Data,
  Methods,
  Computed,
  Props,
  University,
  FilterPathQuery
} from "./types";
import LayoutColumnDouble from "@/components/Atoms/Layout/ColumnDouble/index.vue";
import Heading from "@/components/Atoms/Typography/Heading/index.vue";
import PopoverSelectBox from "@/components/Atoms/Forms/PopoverSelectBox/index.vue";
import FloatingMenu from "@/components/Molecules/FloatingMenu/index.vue";
import FloatingMenuButton from "@/components/Atoms/FloatingMenuItems/FloatingMenuButton/index.vue";
import SearchTextField from "@/components/Atoms/Forms/SearchTextField/index.vue";
import CompanyCard from "@/components/Molecules/CompanyCard/index.vue";
import UniversityTag from "@/components/Pages/Rankings/Favorites/Molecules/UniversityTag/index.vue";
import BusinessCategorySelectBox from "@/components/Molecules/BusinessCategorySelectBox/index.vue";
import { getCompaniesExplorerRank } from "@/api";
import { debounce } from "lodash";
import { BUSINESS_CATEGORY_OPTIONS } from "@/constants/company";
import { thisYear } from "@/helpers/date-helper";
import { ApiRequest } from "@/api/types";
import { Route } from "vue-router/types/router";
import { pathQueryValueToArray } from "@/helpers/router-helper";

const DEFAULT_QUERY: Data["query"] = {
  university_ids: [],
  business_category_name: "",
  graduate_year: 0,
  offset: 0,
  limit: 10
};

const getInitialQuery = (pathQuery: FilterPathQuery | Route["query"]) => {
  let initialQuery = { ...DEFAULT_QUERY };

  const {
    uis: university_ids,
    uns: university_names,
    bcn: business_category_name,
    gy: graduate_year
  } = pathQuery;

  const universityIdsFromPathQuery =
    university_ids &&
    pathQueryValueToArray(university_ids)?.map(id => Number(id));

  const universityNamesFromPathQuery =
    university_names && pathQueryValueToArray(university_names);

  if (
    universityIdsFromPathQuery &&
    universityNamesFromPathQuery &&
    universityIdsFromPathQuery.length === universityNamesFromPathQuery.length
  ) {
    initialQuery.university_ids = universityIdsFromPathQuery;
  }

  const businessCategoryNameFromPathQuery =
    (business_category_name as ApiRequest.BUSINESS_CATEGORY_NAME) || "";
  if (businessCategoryNameFromPathQuery) {
    initialQuery.business_category_name = businessCategoryNameFromPathQuery;
  }

  const graduateYearFromPathQuery = graduate_year ? Number(graduate_year) : 0;
  if (graduateYearFromPathQuery) {
    initialQuery.graduate_year = graduateYearFromPathQuery;
  }

  return initialQuery;
};

const getInitialSelectedUniversities = (
  pathQuery: FilterPathQuery | Route["query"]
) => {
  let initialSelectedUniversities: Data["selectedUniversities"] = [];

  const { uis: university_ids, uns: university_names } = pathQuery;

  const universityIdsFromPathQuery =
    university_ids &&
    pathQueryValueToArray(university_ids)?.map(id => Number(id));

  const universityNamesFromPathQuery =
    university_names && pathQueryValueToArray(university_names);

  if (
    universityIdsFromPathQuery &&
    universityNamesFromPathQuery &&
    universityIdsFromPathQuery.length === universityNamesFromPathQuery.length
  ) {
    initialSelectedUniversities = universityIdsFromPathQuery.map((id, i) => ({
      id: Number(id),
      name: universityNamesFromPathQuery[i]
    }));
  }

  return initialSelectedUniversities;
};

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "RankingFavorite",
  components: {
    LayoutColumnDouble,
    Heading,
    PopoverSelectBox,
    FloatingMenu,
    FloatingMenuButton,
    SearchTextField,
    CompanyCard,
    UniversityTag,
    BusinessCategorySelectBox,
    InfiniteLoading
  },
  props: {
    companies: {
      type: Array as PropType<Props["companies"]>,
      required: true
    },
    universities: {
      type: Array as PropType<Props["universities"]>,
      required: true
    }
  },
  data() {
    return {
      debounceResetAllForBusinessCategoryId: () => {},
      debounceResetAllForUniversities: () => {},
      debounceResetAllForGraduateYear: () => {},
      debounceSearchUniversity: () => {},
      universityNameInput: "",
      universityNamesFromQuery: [],
      query: getInitialQuery(this.$route.query),
      selectedUniversities: getInitialSelectedUniversities(this.$route.query),
      businessCategoryOptions: BUSINESS_CATEGORY_OPTIONS,
      graduateYearOptions: [
        { label: `全卒年`, value: 0 },
        { label: `${thisYear(true) - 2}卒`, value: thisYear() - 2 },
        { label: `${thisYear(true) - 1}卒`, value: thisYear() - 1 },
        { label: `${thisYear(true)}卒`, value: thisYear() }
      ]
    };
  },
  watch: {
    // 検索条件が変わったらoffset等をリセットする
    "query.business_category_name"() {
      this.debounceResetAllForBusinessCategoryId();
    },
    "query.university_ids"() {
      this.debounceResetAllForUniversities();
    },
    "query.graduate_year"() {
      this.debounceResetAllForGraduateYear();
    },
    universityNameInput(val: string) {
      if (!val) return;
      this.debounceSearchUniversity();
    },
    selectedUniversities: {
      deep: true,
      handler(val: University[]) {
        this.query.university_ids = val.map(e => e.id);
      }
    }
  },
  created() {
    // methodsに定義すると動作しないため、created時にdebounceXXXをdataに代入する
    this.initDebounceMethods();
  },
  methods: {
    setPathQuery() {
      const {
        university_ids,
        business_category_name,
        graduate_year
      } = this.query;

      const pathQuery: FilterPathQuery = {
        gy:
          graduate_year !== DEFAULT_QUERY.graduate_year
            ? String(graduate_year)
            : undefined,
        bcn:
          business_category_name !== DEFAULT_QUERY.business_category_name
            ? business_category_name
            : undefined,
        uis: university_ids.length
          ? university_ids.map(id => String(id))
          : undefined,
        uns: university_ids.length
          ? this.selectedUniversities.map(university => university.name)
          : undefined
      };

      this.$router
        .replace({
          name: "RankingsFavorites",
          query: pathQuery
        })
        .catch(err => err);
    },
    async infiniteHandler($state) {
      this.$nextTick(async () => {
        this.setPathQuery();

        const { graduate_year, ...queryWithoutGraduateYear } = this.query;
        const normalizeQuery = this.query.graduate_year
          ? this.query
          : queryWithoutGraduateYear;
        const response = await getCompaniesExplorerRank(normalizeQuery);
        if (response && response.data) {
          const { data } = response;
          if (data.companies && data.companies.length > 0) {
            if (this.query.offset! >= 1) {
              // offsetが1以上の場合は配列にpushする
              this.$emit("pushCompanies", data.companies);
            } else {
              // その他(0)の場合は配列を置き換える
              this.$emit("replaceCompanies", data.companies);
            }
            this.universityNamesFromQuery = data.university_names
              ? data.university_names
              : [];
            $state.loaded();
            // レスポンスが存在する場合はoffsetをずらす
            this.query.offset! += this.query.limit!;
          } else {
            $state.complete();
          }
        } else {
          // undefinedの場合は「該当する検索結果は見つかりません」を表示する
          $state.complete();
        }
      });
    },
    infiniteClear() {
      const infiniteLoading = this.$refs.infiniteLoading as
        | InfiniteLoading
        | undefined;
      infiniteLoading?.stateChanger.reset();
    },
    resetOffset() {
      this.query.offset = 0;
    },
    handleClick(company) {
      const companyDetailRoute = this.$router.resolve({
        name: "CompanyDetail",
        params: { companyId: String(company.id) }
      });
      window.open(companyDetailRoute.href, "_blank", "noopener");
    },
    addUniversityToQuery(university) {
      const alreadyHaveUniversity = this.selectedUniversities.some(
        e => e.id === university.id
      );
      if (alreadyHaveUniversity) {
        this.resetUniversities();
        return;
      }
      this.selectedUniversities.push(university);
      // 大学選択時にTextFieldを空文字に戻す
      this.universityNameInput = "";
      this.resetUniversities();
    },
    removeUniversityToQuery(university) {
      const index = this.selectedUniversities.findIndex(
        e => e.id === university.id
      );
      // array.filterでは変更検知しないため、あえてspliceを使用
      this.selectedUniversities.splice(index, 1);
    },
    resetUniversities() {
      this.$emit("resetUniversities");
    },
    resetAll() {
      this.resetOffset();
      this.infiniteClear();
      this.$emit("resetCompanies");
    },
    initDebounceMethods() {
      this.debounceResetAllForBusinessCategoryId = debounce(() => {
        this.resetAll();
      }, 500);

      this.debounceResetAllForUniversities = debounce(() => {
        this.resetAll();
      }, 500);

      this.debounceResetAllForGraduateYear = debounce(() => {
        this.resetAll();
      }, 500);

      this.debounceSearchUniversity = debounce(() => {
        this.universityNameInput &&
          this.$emit("searchUniversity", this.universityNameInput);
      }, 500);
    }
  }
});
